import { reactive, type Ref, ref } from 'vue'
import { BaseAPIError, ErrorBase } from '@previo/core'
import { $t } from '../../i18n'
import { PToastVariant } from '../../../components/toast/types'
import { generateUniqueId } from './uid'

type ToastProps = {
  title: string
}

export type Toast = ToastProps & {
  id: string
  message: string
  type: PToastVariant
  timeout: number
}

const validate = () => {
  return toasts.value.length < 5
}

const toasts = ref([]) as Ref<Toast[]>

export const toast = reactive({
  get toasts() {
    return toasts.value
  },

  positive(title: string) {
    return toast.show(title, PToastVariant.POSITIVE, 3000)
  },

  warning(title: string) {
    return toast.show(title, PToastVariant.WARNING)
  },

  warnings(warnings: string[] | undefined) {
    if (warnings === undefined || warnings.length === 0) {
      return
    }

    for (const warning of warnings) {
      toast.warning(warning)
    }
  },

  negative(title: string) {
    return toast.show(title, PToastVariant.NEGATIVE)
  },

  info(title: string) {
    return toast.show(title, PToastVariant.INFO)
  },

  error(error: unknown) {
    let msg: string | undefined

    // include the message if the error has a user-friendly message
    if (error instanceof ErrorBase || error instanceof BaseAPIError) {
      msg = error.message
    } else {
      msg = $t('An unexpected application error has occurred.')
    }

    return toast.negative(msg)
  },

  show(message: string, type: PToastVariant, timeout = 5000) {
    if (!validate()) {
      return
    }

    const newToast = {
      id: generateUniqueId(),
      message: message,
      type: type,
      timeout: timeout,
    } as Toast

    toasts.value.push(newToast)

    return newToast
  },

  hide(id: string) {
    toasts.value = toasts.value.filter((toast) => toast.id !== id)
  },
})
